import { useState } from "react";
import { SimpleDialog } from "@/components/ui/simple-dialog";
import { HelpCircle, ChevronDown, ChevronRight, X } from "lucide-react";

const SECTIONS = [
  {
    id: "exec-hangar-timer",
    title: "EXEC HANGAR TIMER",
    rows: [
      {
        label: "What it does",
        text: "Tracks the cooldown timer for the Exec Hangar.",
      },
      {
        label: "How to use it",
        text: "The timer displays the remaining phase cycle time. When it reaches zero it will change to the countdown for the next phase. Use the Sync button to make in the field correction if the default timer is out of sync when you reach the hangar.",
      },
    ],
  },
  {
    id: "board-tracker",
    title: "BOARD TRACKER",
    rows: [
      {
        label: "What it does",
        text: "Tracks Exec Hangar Board 1-7 separately from card counts.",
      },
      {
        label: "How to use it",
        text: "When you claim a board from a timer card it automatically adds a board to the corresponding counter. For corrections or to add boards gathered without a timer you can use the -/+ buttons to alter board counters as needed. Use the bulk actions menu in the header to quickly clear or manage multiple boards at once.",
      },
      {
        label: "Bulk actions",
        text: 'Click the "Bulk" button in the header to access the quick action "Use Set" (decrement all boards by 1).',
      },
    ],
  },
  {
    id: "printers",
    title: "PRINTERS",
    rows: [
      {
        label: "What it does",
        text: "Tracks printer cooldown status.",
      },
      {
        label: "How to use it",
        text: "Quick Add chips labelled by location allow users to add timers for specific cards and boards, timer defaults to 30 mins but can be edited to 1 min resolution if a timer is part way through when arriving at the printer. When a timer runs out users will be presented with a Claim button — this will restart the timer from the default 30 mins and if the timer is for an Exec Hangar board it will add the claim to the appropriate counter. Clicking the claim button will also adjust the user's inventory in the Card Holders tab.",
      },
      {
        label: "Tip",
        text: "If you are claiming a card but you aren't the person who physically collected the card then you can Claim by Proxy — desktop users click the claim button with MMB and mobile device users click with a 1 second hold and you will be presented with a proxy claim dialogue. This will do the same as the regular claim only it will adjust the inventory of the person who actually collected the card.",
      },
    ],
  },
  {
    id: "ship-claims",
    title: "SHIP CLAIMS",
    rows: [
      {
        label: "What it shows",
        text: "A leaderboard of who has claimed the most ships and what they were. The top claimer is highlighted in emerald green; if there are joint highest claimers then the most recent will be highlighted with a bigger, bolder name.",
      },
      {
        label: "How to read it",
        text: "On the left is a chart of how often each ship has dropped in the Exec Hangar, on the right each user's name shows the ships they've claimed and how many times.",
      },
      {
        label: "Sorting",
        text: "You can sort the leaderboard by total claim count or by most recent claim. Use the sort buttons at the top of the dialog.",
      },
      {
        label: "Tip",
        text: "We're watching you, you claim stealing fuckers.",
      },
    ],
  },
  {
    id: "card-holders",
    title: "CARD HOLDERS",
    rows: [
      {
        label: "What it shows",
        text: "A list of all operators and the cards they currently hold claimed in group mode. Cards are organized by type (Red Supervisor, Blue Security Card, etc.).",
      },
      {
        label: "How to use it",
        text: "Overview mode shows how many of each card users have in inventory, sorted by card name (good for finding a specific card). Detailed mode takes a more user-oriented approach sorted by username and giving detail on things like whether or not a card/board was claimed by proxy. Users can delete their own cards after using them by clicking the trash icon next to a card to remove it.",
      },
      {
        label: "Permissions",
        text: "Only the card holder and admin can delete a user's cards.",
      },
      {
        label: "Tip",
        text: "Users are responsible for the accuracy of their own inventory. Using this function to find which user has a particular card may be vulnerable to human error.",
      },
    ],
  },
  {
    id: "recent-claims",
    title: "RECENT CLAIMS",
    rows: [
      {
        label: "What it shows",
        text: "The most recent ship claims in chronological order, with the newest claims appearing first.",
      },
      {
        label: "How to use it",
        text: "Review recent activity to see who claimed what and when. This helps track claim history and verify leaderboard accuracy.",
      },
    ],
  },
];

function HelpCategory({ section, isOpen, onToggle, accentText, accentTextMuted }) {
  return (
    <div className="border border-slate-800/80 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-800/40 transition-colors group"
        aria-expanded={isOpen}
      >
        <span className={`text-[11px] tracking-[0.25em] uppercase text-slate-300 group-hover:${accentText} transition-colors font-medium`}>
          {section.title}
        </span>
        {isOpen ? (
          <ChevronDown className={`w-3.5 h-3.5 ${accentText} flex-shrink-0`} />
        ) : (
          <ChevronRight className={`w-3.5 h-3.5 text-slate-500 group-hover:${accentText} flex-shrink-0 transition-colors`} />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-slate-800/60 px-4 py-3 space-y-3 bg-[#0d1017]/60">
          {section.rows.map((row) => (
            <div key={row.label}>
              <div className={`text-[10px] tracking-[0.3em] uppercase ${accentTextMuted} mb-1`}>
                {row.label}
              </div>
              <p
                className="text-sm text-slate-400 leading-relaxed"
                style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace", fontWeight: 300 }}
              >
                {row.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HelpDialog({ open, onOpenChange, mode }) {
  const [openSections, setOpenSections] = useState({});

  const handleCollapseAll = () => {
    setOpenSections({});
  };

  const isPrivate = mode === "private";
  const accentBorder = isPrivate ? "border-emerald-500/30" : "border-orange-500/30";
  const accentHeaderBorder = isPrivate ? "border-emerald-500/15" : "border-orange-500/15";
  const accentIconBorder = isPrivate ? "border-emerald-500/40" : "border-orange-500/40";
  const accentIconBg = isPrivate ? "bg-emerald-500/5" : "bg-orange-500/5";
  const accentIcon = isPrivate ? "text-emerald-400" : "text-orange-400";
  const accentLabel = isPrivate ? "text-emerald-400" : "text-orange-400";
  const accentCloseHover = isPrivate ? "hover:border-emerald-500 hover:text-emerald-400" : "hover:border-orange-500 hover:text-orange-400";
  const accentCollapseHover = isPrivate ? "hover:border-emerald-500/60 hover:text-emerald-400" : "hover:border-orange-500/60 hover:text-orange-400";
  const accentTextMuted = isPrivate ? "text-emerald-400/80" : "text-orange-400/80";

  return (
    <SimpleDialog open={open} onOpenChange={onOpenChange} className="max-w-lg">
      <div
        className={`bg-[#0B0E14]/95 backdrop-blur-xl border ${accentBorder} rounded-none text-white w-full max-h-[85vh] flex flex-col`}
        data-testid="help-dialog"
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${accentHeaderBorder} flex-shrink-0`}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 border ${accentIconBorder} flex items-center justify-center ${accentIconBg}`}>
              <HelpCircle className={`w-3.5 h-3.5 ${accentIcon}`} />
            </div>
            <div>
              <div className={`text-[10px] tracking-[0.3em] uppercase ${accentLabel} mb-0.5`}>
                Documentation
              </div>
              <div className="font-display font-bold text-base tracking-tight text-slate-200">
                Help &amp; Guide
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close help"
            className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentCloseHover} transition-colors`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Collapse All toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800/60 flex-shrink-0 bg-[#0d1017]/40">
          <span className="text-[10px] tracking-[0.25em] uppercase text-slate-600">
            {SECTIONS.length} categories
          </span>
          <button
            type="button"
            onClick={handleCollapseAll}
            className={`flex items-center gap-1.5 px-2.5 py-1 border border-slate-700 text-[10px] tracking-widest uppercase text-slate-400 ${accentCollapseHover} transition-colors`}
          >
            <ChevronDown className="w-3 h-3" />
            Collapse All
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
          {SECTIONS.map((section) => (
            <HelpCategory
              key={section.id}
              section={section}
              isOpen={!!openSections[section.id]}
              onToggle={() =>
                setOpenSections((prev) => ({
                  ...prev,
                  [section.id]: !prev[section.id],
                }))
              }
              accentText={accentLabel}
              accentTextMuted={accentTextMuted}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-800/60 flex-shrink-0">
          <div className="text-[10px] text-slate-600 tracking-wide">
            EXEC/HANGAR — Card Cooldown Tracker
          </div>
        </div>
      </div>
    </SimpleDialog>
  );
}
